import React, { useState } from 'react';
import { PageTransition } from '../transitions/PageTransition';
import { Santri } from '../../types';
import { useAppVersion } from '../../hooks/useAppVersion';

interface PortalWaliProfilProps {
  santri: Santri;
  onNavigate: (view: string) => void;
  onLogout: () => void;
}

export const PortalWaliProfil: React.FC<PortalWaliProfilProps> = ({ santri, onNavigate, onLogout }) => {
  const { currentVersion, buildId, isUpdateAvailable, latestVersionData, handleForceUpdateApp } = useAppVersion();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const handleUpdateClick = async () => {
    setIsUpdating(true);
    // Tombol di Profil SELALU memperbarui total: hapus semua cache + service
    // worker lalu unduh ulang seluruh kode aplikasi. Ini jalur antisipasi bila
    // aplikasi di perangkat usang / error. Pembaruan cepat (hanya file yang
    // berubah) dilakukan lewat modal "Perbarui Sekarang" (ReloadPrompt).
    await handleForceUpdateApp({ clearCache: true });
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-[#f6f8f6] text-[#111811] font-sans antialiased pb-24">
        <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden pb-24">
          <div className="bg-white rounded-b-3xl shadow-sm pb-8">
            <div className="flex items-center justify-between p-4 pt-6">
              <div className="w-10"></div>
              <h2 className="text-xl font-bold tracking-tight text-[#111811]">Profil</h2>
              <button className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f6f8f6] text-[#111811] transition hover:bg-gray-200">
                <span className="material-symbols-outlined">settings</span>
              </button>
            </div>
            <div className="px-4 mt-2">
              <div className="flex flex-col items-center justify-center text-center p-6">
                <div className="relative mb-4">
                  <div
                    className="bg-center bg-no-repeat bg-cover rounded-full h-28 w-28 border-4 border-white shadow-xl"
                    style={{backgroundImage: `url("${santri.foto || 'https://placehold.co/150x150'}")`}}
                  ></div>
                  <div className="absolute bottom-0 right-0 bg-[#40513B] text-white p-1.5 rounded-full shadow-sm border-2 border-white">
                    <span className="material-symbols-outlined text-sm font-bold block" style={{fontSize: '16px'}}>verified</span>
                  </div>
                </div>
                <h3 className="text-2xl font-bold leading-tight mb-1 text-[#111811]">{santri.nama}</h3>
                <div className="flex items-center gap-2 mb-2 justify-center">
                  <span className="bg-[#40513B]/10 px-3 py-1 rounded-full text-xs font-semibold text-[#40513B]">{santri.kelas}</span>
                  <span className="bg-green-100 px-3 py-1 rounded-full text-xs font-semibold text-green-700">Santri Aktif</span>
                </div>
                <p className="text-gray-500 text-sm font-medium">Wali Kelas: {santri.waliKelas || '-'}</p>
              </div>
            </div>
          </div>
          <div className="px-4 mt-6 space-y-4">
            <button
              onClick={() => onNavigate('info')}
              className="w-full flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600 group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined">person</span>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-[#111811] text-base">Informasi Pribadi</h4>
                  <p className="text-xs text-gray-500">Detail data diri dan akademik</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-400">chevron_right</span>
            </button>
            <button className="w-full flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors group">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined">folder_open</span>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-[#111811] text-base">Berkas</h4>
                  <p className="text-xs text-gray-500">Dokumen pendukung santri</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-400">chevron_right</span>
            </button>
            <button className="w-full flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors group">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-orange-600 group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined">lock_reset</span>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-[#111811] text-base">Ganti Kata Sandi</h4>
                  <p className="text-xs text-gray-500">Amankan akun anda</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-400">chevron_right</span>
            </button>
            <button className="w-full flex items-center justify-between bg-white p-4 rounded-2xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors group">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-50 text-purple-600 group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined">help_center</span>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-[#111811] text-base">Pusat Bantuan</h4>
                  <p className="text-xs text-gray-500">FAQ dan kontak admin</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-400">chevron_right</span>
            </button>

            {/* Tombol Perbarui Aplikasi */}
            <button
              onClick={handleUpdateClick}
              disabled={isUpdating}
              className={`w-full flex items-center justify-between p-4 rounded-2xl shadow-sm border transition-colors group ${
                isUpdateAvailable
                  ? 'bg-amber-50 border-amber-200 hover:bg-amber-100 animate-pulse'
                  : 'bg-white border-gray-100 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full transition-transform ${
                  isUpdateAvailable ? 'bg-amber-100 text-amber-700' : 'bg-cyan-50 text-cyan-600 group-hover:scale-110'
                }`}>
                  <span className={`material-symbols-outlined ${isUpdating ? 'animate-spin' : ''}`}>
                    {isUpdating ? 'sync' : (isUpdateAvailable ? 'system_update' : 'refresh')}
                  </span>
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-[#111811] text-base">
                      {isUpdateAvailable ? 'Pembaruan Tersedia!' : 'Perbarui Aplikasi'}
                    </h4>
                    {isUpdateAvailable && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-white">
                        v{latestVersionData?.version}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {isUpdating
                      ? 'Membersihkan cache & memuat ulang...'
                      : (isUpdateAvailable
                        ? 'Versi baru sudah siap. Ketuk untuk memperbarui total'
                        : 'Muat ulang sistem & bersihkan cache browser')}
                  </p>
                </div>
              </div>
              <span className="material-symbols-outlined text-gray-400">cached</span>
            </button>

            <button
              onClick={onLogout}
              className="w-full flex items-center justify-between bg-red-50 p-4 rounded-2xl shadow-sm border border-red-100 hover:bg-red-100 transition-colors group mt-6"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600 group-hover:scale-110 transition-transform">
                  <span className="material-symbols-outlined">logout</span>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-red-700 text-base">Keluar</h4>
                  <p className="text-xs text-red-500">Keluar dari aplikasi</p>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-8 mb-4 text-center">
            <div className="flex items-center justify-center gap-1.5">
              <p className="text-[11px] font-medium text-gray-500">Portal MIAS v{currentVersion} <span className="text-gray-300">· {buildId}</span></p>
              {isUpdateAvailable && (
                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-ping" title="Versi baru tersedia" />
              )}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">© {new Date().getFullYear()} Ma'had Ihya As-Sunnah</p>
          </div>

          <div className="h-24"></div>
        </div>
      </div>
    </PageTransition>
  );
};
